// One row of the org-chart tree view (components/OrgChartModal.js Chart
// tab) — a person + their direct reports, indented one level further.
// Recursive by self-reference: Children.childExtends: 'OrgChartTreeNode'.
// Seeded per-instance via childrenAs: 'state' (see OrgChartModal.js and the
// Children block below), so `s` here is the row's own {partyId, name,
// avatar, depth}, and `s.root` still reaches the shared app state.
export const OrgChartTreeNode = {
  flow: 'y',
  gap: 'Y',

  Row: {
    flow: 'x',
    align: 'center',
    gap: 'Y',

    // Built-in Avatar is a bare `<img>` (extends smbls.Avatar) — an empty
    // `src` renders the browser's broken-image glyph rather than falling
    // back gracefully. Most Party rows have no avatar image, so this needs
    // the same image-or-initials treatment PersonCard.js's PcAvatar uses
    // (without its presence-dot overlay, not relevant here).
    PersonAvatar: {
      align: 'center center',
      boxSize: 'C',
      round: '50%',
      color: 'title',
      fontSize: 'Y2',
      fontWeight: '700',
      position: 'relative',
      overflow: 'hidden',
      background: 'overlayStrong',
      AvatarImg: {
        tag: 'img',
        // `if:`, never `hide:` — a display:none <img> still FETCHES its src;
        // non-URL avatar values 404ed against the origin (same class as the
        // IntranetRows plate, fixed 2026-09-01). Guarded src as the belt.
        if: (el, s) => typeof s.avatar === 'string' && /^(https?:|\/)/.test(s.avatar),
        position: 'absolute',
        inset: '0',
        width: '100%',
        height: '100%',
        src: (el, s) =>
          typeof s.avatar === 'string' && /^(https?:|\/)/.test(s.avatar) ? s.avatar : null,
        alt: (el, s) => s.name || '',
        style: { objectFit: 'cover' }
      },
      Initials: {
        tag: 'span',
        hide: (el, s) => typeof s.avatar === 'string' && /^(https?:|\/)/.test(s.avatar),
        text: (el, s) => {
          const parts = String(s.name || '')
            .trim()
            .split(/\s+/)
            .filter(Boolean)
          if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
          return String(s.name || '?')
            .slice(0, 2)
            .toUpperCase()
        }
      }
    },
    Name: {
      tag: 'span',
      fontSize: 'Z2',
      fontWeight: (el, s) => (s.depth ? '500' : '700'),
      color: 'title',
      text: (el, s) => s.name || ''
    }
  },

  Children: {
    paddingLeft: 'C',
    flow: 'y',
    gap: 'Y',
    hide: (el, s) => !(el.call('orgChartChildrenOf').get(s.partyId) || []).length,
    childExtends: 'OrgChartTreeNode',
    childrenAs: 'state',
    children: (el, s) => {
      const byId = new Map(el.call('orgChartDirectory').map((p) => [p.partyId, p]))
      const kidIds = el.call('orgChartChildrenOf').get(s.partyId) || []
      return kidIds
        .map((id) => byId.get(id))
        .filter(Boolean)
        .map((p) => ({ ...p, depth: (s.depth || 0) + 1 }))
    }
  }
}
